import { createFileRoute, Link, Outlet, useMatchRoute } from '@tanstack/react-router';
import { useState, useMemo, Suspense, lazy } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  MailIcon,
  CalendarIcon,
  FileTextIcon,
  TargetIcon,
  MessageSquareIcon,
  ActivityIcon,
  EyeIcon,
  EyeOffIcon,
  BriefcaseIcon,
  PhoneIcon,
  ShieldIcon,
} from 'lucide-react';
import { format } from 'date-fns';
import { RoleBadge, StatusBadge, PerformanceCard } from '@/components/employees';
import { useEmployee } from '@/lib/hooks/useEmployees';
import { Loader2Icon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { GoalFormSheet } from '@/components/goals/GoalFormSheet';
import { useCreateGoal } from '@/hooks/use-goals';
import type { CreateGoalRequest } from '@/lib/api/goals';
import { FeedbackFormSheet } from '@/components/feedback/FeedbackFormSheet';
import { useCreateFeedback } from '@/hooks/use-feedback';
import type { CreateFeedbackRequest } from '@/lib/api/feedback';

const PerformanceChart = lazy(() =>
  import('@/components/employees/PerformanceChart').then(module => ({ default: module.PerformanceChart }))
);

export const Route = createFileRoute('/admin/employees/$id')({
  component: EmployeeLayout,
});

function EmployeeLayout() {
  const { id } = Route.useParams();
  const { data: employee, isLoading, error } = useEmployee(id);
  const matchRoute = useMatchRoute();
  const [showMetrics, setShowMetrics] = useState(false);
  const [showGoalSheet, setShowGoalSheet] = useState(false);
  const [showFeedbackSheet, setShowFeedbackSheet] = useState(false);

  const createGoalMutation = useCreateGoal(id);
  const createFeedbackMutation = useCreateFeedback(id);
  const trends: import('@repo/types').PerformanceTrend[] = [];

  const handleCreateGoal = async (data: CreateGoalRequest) => {
    await createGoalMutation.mutateAsync(data);
    setShowGoalSheet(false);
  };

  const handleCreateFeedback = async (data: CreateFeedbackRequest) => {
    await createFeedbackMutation.mutateAsync(data);
    setShowFeedbackSheet(false);
  };

  const formattedDates = useMemo(() => {
    if (!employee) return null;
    return {
      joined: format(employee.joinedAt, 'MMM dd, yyyy'),
      lastActive: format(employee.lastActive, 'MMM dd, yyyy h:mm a'),
    };
  }, [employee?.joinedAt, employee?.lastActive]);

  const isOnJobs = Boolean(matchRoute({ to: '/admin/employees/$id/jobs', params: { id } }));
  const isOnGoals = Boolean(matchRoute({ to: '/admin/employees/$id/goals', params: { id } }));
  const isOnFeedback = Boolean(matchRoute({ to: '/admin/employees/$id/feedback', params: { id } }));


  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2Icon className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Employee Not Found</h2>
          <p className="text-muted-foreground mt-2">The employee with ID {id} does not exist.</p>
        </div>
      </div>
    );
  }

  const initials = employee.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="flex flex-col gap-6 p-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
              <div className="flex gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarFallback className="text-lg">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-3">
                  <div>
                    <h1 className="text-2xl font-bold">{employee.name}</h1>
                    <div className="flex items-center gap-2 mt-1">
                      <RoleBadge role={employee.role} />
                      <StatusBadge status={employee.status} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <MailIcon className="h-4 w-4" />
                      {employee.email}
                    </div>
                    <div className="flex items-center gap-2">
                      <PhoneIcon className="h-4 w-4" />
                      {employee.phone}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-1.5 text-sm text-muted-foreground md:text-right">
                {employee.team && (
                  <div className="flex items-center gap-2 md:justify-end">
                    <FileTextIcon className="h-4 w-4" />
                    <span>Team: {employee.team.name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 md:justify-end">
                  <ActivityIcon className="h-4 w-4" />
                  <span>Last active: {formattedDates?.lastActive}</span>
                </div>
                {employee.permissions && employee.permissions.length > 0 && (
                  <div className="flex items-center gap-2 md:justify-end">
                    <ShieldIcon className="h-4 w-4" />
                    <span>Permissions: {employee.permissions.length}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t" />

            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CalendarIcon className="h-4 w-4" />
                <span>Joined {formattedDates?.joined}</span>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground mr-2">
                  Quick Actions:
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowGoalSheet(true)}
                >
                  <TargetIcon className="mr-2 h-4 w-4" />
                  Add Goal
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFeedbackSheet(true)}
                >
                  <MessageSquareIcon className="mr-2 h-4 w-4" />
                  Add Feedback
                </Button>
                <Button
                  variant={showMetrics ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowMetrics(!showMetrics)}
                  className="ml-2"
                >
                  {showMetrics ? (
                    <>
                      <EyeOffIcon className="mr-2 h-4 w-4" />
                      Hide Metrics
                    </>
                  ) : (
                    <>
                      <EyeIcon className="mr-2 h-4 w-4" />
                      Show Metrics
                    </>
                  )}
                </Button>
              </div>
            </div>

            {showMetrics && (
              <div className="pt-4 space-y-4 animate-accordion-down">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-4">
                  <PerformanceCard
                    title="Jobs Posted"
                    value={employee.metrics.totalJobsPosted}
                    change={5.2}
                    trend="up"
                    description="from last period"
                  />
                  <PerformanceCard
                    title="Quality Score"
                    value={`${employee.metrics.avgQualityScore}%`}
                    change={2.1}
                    trend="up"
                    description="from last period"
                  />
                  <PerformanceCard
                    title="Customer Feedback"
                    value={employee.metrics.customerFeedback.toFixed(1)}
                    change={0.3}
                    trend="up"
                    description="out of 5.0"
                  />
                  <PerformanceCard
                    title="Response Time"
                    value={`${employee.metrics.responseTime}h`}
                    change={-10}
                    trend="down"
                    description="from last period"
                  />
                  <PerformanceCard
                    title="Duplicate Rate"
                    value={`${employee.metrics.duplicateRate}%`}
                    change={-0.5}
                    trend="down"
                    description="from last period"
                  />
                  <PerformanceCard
                    title="This Month"
                    value={employee.metrics.jobsThisMonth}
                    description="jobs posted"
                  />
                </div>

                <Suspense
                  fallback={
                    <div className="h-[400px] flex items-center justify-center text-sm text-muted-foreground">
                      Loading chart...
                    </div>
                  }
                >
                  <PerformanceChart
                    data={trends}
                    title="Performance Trends"
                    description="12-week performance overview"
                  />
                </Suspense>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="h-full overflow-auto">
        <div className="space-y-6">
          <div className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground">
            <Link
              to="/admin/employees/$id/jobs"
              params={{ id }}
              className={cn(
                "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                isOnJobs && "bg-background text-foreground shadow-sm"
              )}
            >
              <BriefcaseIcon className="mr-2 h-4 w-4" />
              Recent Jobs
            </Link>
            <Link
              to="/admin/employees/$id/goals"
              params={{ id }}
              className={cn(
                "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                isOnGoals && "bg-background text-foreground shadow-sm"
              )}
            >
              <TargetIcon className="mr-2 h-4 w-4" />
              Goals
            </Link>
            <Link
              to="/admin/employees/$id/feedback"
              params={{ id }}
              className={cn(
                "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                isOnFeedback && "bg-background text-foreground shadow-sm"
              )}
            >
              <MessageSquareIcon className="mr-2 h-4 w-4" />
              Feedback
            </Link>
          </div>

          <Outlet />
        </div>
      </div>

      {/* Goal Form Sheet */}
      <GoalFormSheet
        open={showGoalSheet}
        onOpenChange={setShowGoalSheet}
        employeeId={id}
        onSubmit={handleCreateGoal}
        isSubmitting={createGoalMutation.isPending}
      />

      {/* Feedback Form Sheet */}
      <FeedbackFormSheet
        open={showFeedbackSheet}
        onOpenChange={setShowFeedbackSheet}
        employeeId={id}
        onSubmit={handleCreateFeedback}
        isSubmitting={createFeedbackMutation.isPending}
      />
    </div>
  );
}
